// ─── Colombia · creación de cobro (Bre-B QR · PSE · Nequi push · Botón Bancolombia) ───
// The Colombia tab of `/app/payins` runs the SAME flow as the COP wallet deposit modal, from the same
// components — only the chrome differs (a page card here, a dialog there) and the copy, which is the
// collector's rather than the wallet owner's.
//
//   Bre-B  → amount + method, then create. A dynamic QR needs no payer identity, so nothing else is asked
//            and the host page shows its success screen.
//   PSE    → amount + method → payer → confirm → create → automatic handoff to the payer's bank.
//   Nequi  → same steps; the outcome is a wait state, because the charge lands in the payer's app.
//   Botón  → same steps; the outcome is the same bank handoff as PSE, minus the bank picker.
//
// Everything but Bre-B is created HERE rather than by the host page, because their outcome is not a screen
// the host can render: two of them replace the document with the bank's, and `PayinReturnPage` picks the
// operation back up on the way in. Only Bre-B hands a value back through `onSubmit`.
//
// The branch below is `colombiaMethodNeedsPayer(method)` — a negation of Bre-B, never an enumeration of the
// payer rails. Enumerating is what silently routed Nequi and Botón Bancolombia through the Bre-B builder.

import {
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  Landmark,
  Loader2,
  MousePointerClick,
  QrCode,
  Save,
  Smartphone,
  XCircle,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { newIdempotencyKey } from "@/modules/dashboard/dispersions/services/paymentsPayout.service";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaStepper, type ColocaStepperStep } from "@/shared/components/navigation/ColocaStepper";
import { BrebReceivingKeySelect } from "@/shared/components/payments/breb-key/BrebReceivingKeySelect";
import { type ColombiaPayerValues, ColombiaPayerStep } from "@/shared/components/payments/ColombiaPayerStep";
import { ColombiaPayinMethodChoice } from "@/shared/components/payments/ColombiaPayinMethodChoice";
import {
  ALL_COLOMBIA_PAYIN_METHODS,
  COLOMBIA_REDIRECT_METHODS,
  type ColombiaPayinMethod,
  colombiaMethodNeedsPayer,
} from "@/shared/components/payments/colombiaPayinMethods";
import { NequiWaitingScreen } from "@/shared/components/payments/NequiWaitingScreen";
import {
  PayinAmountWithFee,
  type PayinAmountWithFeeState,
} from "@/shared/components/payments/PayinAmountWithFee";
import { type PayinConfirmRow, PayinConfirmSummary } from "@/shared/components/payments/PayinConfirmSummary";
import { quickAmountsFor } from "@/shared/components/payments/payinQuickAmounts";
import {
  PseConfirmedScreen,
  PseFailureScreen,
  PseHandoffScreen,
} from "@/shared/components/payments/pse/PseHandoffScreens";
import { psePayerUserType, type PsePayerValues } from "@/shared/components/payments/pse/psePayer.schema";
import { PsePayerStep } from "@/shared/components/payments/pse/PsePayerStep";
import { usePseRedirect } from "@/shared/components/payments/pse/usePseRedirect";
import { useAvailableCorridors } from "@/shared/hooks/useAvailableCorridors";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import {
  boundsForPayer,
  formatLimitAmount,
  payerTypeForColombianPayer,
  resolveAmountLimitViolation,
} from "@/shared/utils/amountLimits";
import { formatCurrencyAmount } from "@/shared/utils/currencyMinorUnits";
import { formatRail } from "@/shared/utils/railDisplay";

import { usePayinAmountLabels, usePayinRailHint } from "../../hooks/usePayinAmountLabels";
import { isTerminalPayinStatus, usePayinLiveStatus } from "../../hooks/usePayinLiveStatus";
import {
  buildColombiaBancolombiaButtonPayinPayload,
  buildColombiaNequiPayinPayload,
  buildColombiaPsePayinPayload,
  type InitiatePayinPayload,
  type InitiatePayinResponse,
  PayinRail,
  PayinRuntimeStatus,
  PaymentsPayinService,
  readPayinErrorCode,
} from "../../services/paymentsPayin.service";

/**
 * The payer, in the widest shape any Colombian rail asks for.
 *
 * PSE collects two fields the others do not — the bank and the person type — so they are optional here and a
 * Nequi or Botón Bancolombia payer simply omits them. `personType` is left UNSET rather than defaulted,
 * because `payerTypeForColombianPayer` reads it first and falls back to "document type is NIT" only when it
 * is absent: defaulting it to `"natural"` would classify every company paying by Nequi as an individual and
 * check the amount against the wrong limit.
 */
type ColombiaPayer = ColombiaPayerValues & Partial<Pick<PsePayerValues, "personType" | "bankCode">>;

export interface PayinColombiaFormValues {
  /** Gross COP amount the payer transfers, as the preview resolved it (decimal string). */
  grossAmount: string;
  /** Another ACTIVE organization key to mint the QR over; null keeps the primary one. */
  brebReceivingKeyId: string | null;
}

export interface PayinColombiaFormProps {
  /** Org the payin is created for — also scopes the fee preview to its effective price book. */
  organizationId: string | null;
  /** Receives validated Bre-B values; the host page builds the runtime payload and performs the request. */
  onSubmit: (values: PayinColombiaFormValues) => void | Promise<void>;
  isSubmitting: boolean;
}

/** The payer form lives in the card body, its submit button under it — wired by this id. */
const PAYER_FORM_ID = "payins-colombia-payer";

/** Rail → the icon that opens the amount step. Bre-B is a QR, PSE and Botón are banks, Nequi is a phone. */
const METHOD_ICON = {
  [PayinRail.BREB]: QrCode,
  [PayinRail.PSE]: Landmark,
  [PayinRail.NEQUI]: Smartphone,
  [PayinRail.BANCOLOMBIA_BUTTON]: MousePointerClick,
} as const;

const EMPTY_PRICED: PayinAmountWithFeeState = {
  mode: "gross",
  preview: null,
  amount: null,
  loading: false,
  error: null,
};

type ColombiaStep = "amount" | "payer" | "confirm" | "creating" | "handoff";

/**
 * Outcome screens for a payer rail that has no bank page of its own — today only Nequi.
 *
 * The PSE pair cannot be reused: `PseFailureScreen` renders that network's mandated wording for its own
 * detail codes and its merchant contact lines, and `PseConfirmedScreen` says the payer "authorised the
 * debit", which is what happens at a bank page and not what happens when someone accepts a push.
 */
function RailFailureScreen({ message, onRetry }: { message?: string; onRetry: () => void }) {
  const { t } = useTranslations("payins");
  return (
    <div className="flex flex-col items-center gap-4 py-14 text-center">
      <div className="flex size-14 items-center justify-center rounded-full bg-destructive/10">
        <XCircle className="size-7 text-destructive" aria-hidden="true" />
      </div>
      <div>
        <p className="font-semibold text-destructive">{t("rail_failure_title")}</p>
        <p className="mt-1 max-w-sm text-sm text-muted-foreground">{message ?? t("rail_failure_generic")}</p>
      </div>
      <ColocaButton variant="primary" onClick={onRetry}>
        {t("rail_failure_retry")}
      </ColocaButton>
    </div>
  );
}

function RailConfirmedScreen({ onNew }: { onNew: () => void }) {
  const { t } = useTranslations("payins");
  return (
    <div className="flex flex-col items-center gap-4 py-14 text-center">
      <div className="flex size-14 items-center justify-center rounded-full bg-success/10">
        <CheckCircle2 className="size-7 text-success" aria-hidden="true" />
      </div>
      <div>
        <p className="font-semibold text-foreground">{t("rail_confirmed_title")}</p>
        <p className="mt-1 max-w-sm text-sm text-muted-foreground">{t("rail_confirmed_body")}</p>
      </div>
      <ColocaButton variant="primary" onClick={onNew}>
        {t("rail_confirmed_new")}
      </ColocaButton>
    </div>
  );
}

export function PayinColombiaForm({ organizationId, onSubmit, isSubmitting }: PayinColombiaFormProps) {
  const { t, activeLanguage } = useTranslations("payins");
  const labels = usePayinAmountLabels();

  const [step, setStep] = useState<ColombiaStep>("amount");
  const [method, setMethod] = useState<ColombiaPayinMethod>(PayinRail.BREB);
  const [brebKeyId, setBrebKeyId] = useState<string | null>(null);
  const [priced, setPriced] = useState<PayinAmountWithFeeState>(EMPTY_PRICED);
  const [payer, setPayer] = useState<ColombiaPayer | null>(null);
  const [bankName, setBankName] = useState("");
  const [idempotencyKey, setIdempotencyKey] = useState<string>(() => newIdempotencyKey());
  const [createdPayin, setCreatedPayin] = useState<InitiatePayinResponse | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createErrorCode, setCreateErrorCode] = useState<string | null>(null);

  const railHint = usePayinRailHint(method);
  const isPse = method === PayinRail.PSE;
  const isNequi = method === PayinRail.NEQUI;
  const needsPayer = colombiaMethodNeedsPayer(method);
  const isRedirect = COLOMBIA_REDIRECT_METHODS.has(method);
  const reference = idempotencyKey.replace(/-/g, "").slice(0, 10).toUpperCase();
  const formatCop = useCallback(
    (value: string | null | undefined) => formatCurrencyAmount(value, "COP", activeLanguage),
    [activeLanguage],
  );

  /**
   * The payer as the CURRENT rail defines it.
   *
   * The three payer rails share one field contract, so walking back from the payer step and switching rails
   * keeps what was typed — a deliberate convenience. But two of those fields are PSE's alone, and
   * `payerTypeForColombianPayer` reads `personType` first: a legal entity typed for PSE would keep being
   * classified as a company after switching to Nequi, checking the amount against the wrong limit. Derived
   * rather than cleared on change, so it cannot desync however the state was reached.
   */
  const effectivePayer = useMemo<ColombiaPayer | null>(
    () => (payer && !isPse ? { ...payer, personType: undefined, bankCode: undefined } : payer),
    [payer, isPse],
  );

  // The amount is typed before the payer says whether they are a person or a company, so the amount step
  // can only check the envelope of both kinds; the exact cap is re-checked here once the payer is known.
  const corridors = useAvailableCorridors(organizationId ?? undefined);
  const { payerType, confirmBounds, confirmViolation } = useMemo(() => {
    const kind = effectivePayer ? payerTypeForColombianPayer(effectivePayer) : null;
    const bounds = boundsForPayer(corridors.limitsFor(method, "COL", "COP", "PAYIN"), kind);
    return {
      payerType: kind,
      confirmBounds: bounds,
      confirmViolation: priced.preview
        ? resolveAmountLimitViolation(priced.preview.gross_amount, bounds)
        : null,
    };
  }, [corridors, method, effectivePayer, priced.preview]);

  // Live for the same reason the wallet modal is: the webhook can settle or reject the payin while the payer
  // is still at their bank — or still holding their phone — and the outcome screen must follow it.
  const liveStatus = usePayinLiveStatus(createdPayin?.id ?? null, { expiresAt: createdPayin?.expires_at });
  const isTerminal = isTerminalPayinStatus(liveStatus);
  // Safe to call for every rail: the hook gates on the PAYIN's own rail, so a Nequi collection never
  // navigates anywhere. That guard is the hook's, not the caller's, which is why it is not passed a flag.
  const { failureDetail } = usePseRedirect({ payin: createdPayin, bankName, reference, isTerminal });

  const resetFlow = useCallback(() => {
    setStep("amount");
    setPriced(EMPTY_PRICED);
    setPayer(null);
    setBankName("");
    setCreatedPayin(null);
    setCreateError(null);
    setCreateErrorCode(null);
    setIdempotencyKey(newIdempotencyKey());
  }, []);

  const handleBrebSubmit = useCallback(() => {
    if (!priced.preview) return;
    void onSubmit({ grossAmount: priced.preview.gross_amount, brebReceivingKeyId: brebKeyId });
  }, [priced.preview, onSubmit, brebKeyId]);

  const handlePsePayerSubmit = useCallback((values: PsePayerValues, meta: { bankName: string }) => {
    setPayer(values);
    setBankName(meta.bankName);
    setStep("confirm");
  }, []);

  // Nequi and Botón Bancolombia collect the same identity minus PSE's bank and person type. Stored as-is:
  // see `ColombiaPayer` on why `personType` must stay absent rather than be defaulted.
  const handleColombiaPayerSubmit = useCallback((values: ColombiaPayerValues) => {
    setPayer(values);
    setBankName("");
    setStep("confirm");
  }, []);

  const handleCreate = useCallback(() => {
    if (!organizationId || !priced.preview || !payer || confirmViolation) return;

    const common = {
      organizationId,
      amountCop: priced.preview.gross_amount,
      payerName: payer.payerName,
      documentType: payer.documentType,
      documentNumber: payer.documentNumber,
      email: payer.email,
      phone: payer.phone,
      address: payer.address,
      idempotencyKey,
    };

    // One payload per rail. The method reaching this switch is the whole point of the fix: it used to stop
    // at the submit branch, and everything that was not PSE was created as Bre-B.
    let payload: InitiatePayinPayload;
    if (method === PayinRail.PSE) {
      payload = buildColombiaPsePayinPayload({
        ...common,
        bankCode: payer.bankCode ?? "",
        userType: psePayerUserType(payer.personType ?? "natural"),
      });
    } else if (method === PayinRail.NEQUI) {
      payload = buildColombiaNequiPayinPayload(common);
    } else {
      payload = buildColombiaBancolombiaButtonPayinPayload(common);
    }

    setStep("creating");
    setCreateError(null);
    setCreateErrorCode(null);
    PaymentsPayinService.initiatePayinOnce(payload)
      .then((response) => {
        setCreatedPayin(response.data);
        setStep("handoff");
      })
      .catch((err: unknown) => {
        setCreateError(HttpApiService.getErrorMessage(err) || t("error_create"));
        setCreateErrorCode(readPayinErrorCode(err));
        setStep("handoff");
      });
  }, [organizationId, priced.preview, payer, confirmViolation, idempotencyKey, method, t]);

  // Only the payer rails have a middle step, and the stepper says so rather than showing a phantom one.
  const stepperSteps: ColocaStepperStep[] = [
    { label: t("step_amount") },
    { label: t("step_payer") },
    { label: t("step_confirm") },
  ];
  const stepperCurrent = step === "amount" ? 0 : step === "payer" ? 1 : 2;

  // ── The outcome: on the way to the bank, waiting in the payer's app, settled, or never got there. ──
  //
  // `PseFailureScreen` and `PseConfirmedScreen` carry PSE's OWN copy — the network's mandated wording for
  // `FAIL_EXCEEDEDLIMIT`, its merchant contact lines — so they are shown to the redirect rails only. Nequi
  // gets a rail-neutral pair instead, which is the same split the COP wallet modal makes.
  if (step === "handoff") {
    if (createError) {
      return isRedirect ? (
        <PseFailureScreen detail={createErrorCode} message={createError} onRetry={resetFlow} />
      ) : (
        <RailFailureScreen message={createError} onRetry={resetFlow} />
      );
    }
    if (isTerminal && !isRedirect) {
      return liveStatus === PayinRuntimeStatus.CONFIRMED ? (
        <RailConfirmedScreen onNew={resetFlow} />
      ) : (
        <RailFailureScreen onRetry={resetFlow} />
      );
    }
    if (isTerminal) {
      // A redirect rail only: the branch above already took every rail that has no bank page. Terminal cuts
      // both ways and only one of them is a failure — `usePseRedirect` refuses to navigate to a payin that
      // can no longer move, so a collection that settles before the redirect fires ends its life right here,
      // and telling that collector their charge is still "being created" would be a lie about money that
      // already landed.
      return liveStatus === PayinRuntimeStatus.CONFIRMED ? (
        <PseConfirmedScreen onNew={resetFlow} />
      ) : (
        <PseFailureScreen detail={failureDetail} onRetry={resetFlow} />
      );
    }
    // Nequi hands nothing over: the charge is already in the payer's app, so this is a wait state rather
    // than an instrument. The live-status hook above flips it the moment the webhook lands.
    if (isNequi) {
      return (
        <NequiWaitingScreen
          title={t("nequi_waiting_title")}
          body={t("nequi_waiting_body", { phone: payer?.phone ?? "" })}
          liveLabel={t("nequi_waiting_live")}
          amount={priced.preview ? `${formatCop(priced.preview.gross_amount)} COP` : null}
          reference={reference}
          referenceLabel={t("pse_voucher_reference_label")}
          actionLabel={t("nequi_waiting_new")}
          onNew={resetFlow}
        />
      );
    }
    // The redirect rails wait for the bank page; `usePseRedirect` takes the payer there the moment the URL
    // exists. Bre-B never reaches this step at all — it returns through `onSubmit` and the host renders it.
    return isRedirect ? <PseHandoffScreen onCancel={resetFlow} /> : null;
  }

  if (step === "creating") {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-16">
        <Loader2
          className="size-10 animate-spin text-primary motion-reduce:animate-none"
          aria-hidden="true"
        />
        <p className="text-sm text-muted-foreground">{t("pse_handoff_title")}</p>
      </div>
    );
  }

  // ── Confirmation ──
  if (step === "confirm" && priced.preview && payer) {
    const rows: PayinConfirmRow[] = [
      // Not the `breb_success_*` strings: those are the success screen's `label:` sentences, colon included,
      // and this summary renders labels bare and uppercased.
      { label: t("confirm_amount"), value: `${formatCop(priced.preview.gross_amount)} COP` },
      { label: t("fee_preview_fee"), value: `${formatCop(priced.preview.fee_total)} COP` },
      { label: t("confirm_net"), value: `${formatCop(priced.preview.net_amount)} COP` },
      { label: t("form_method_label"), value: isPse ? t("method_pse_title") : formatRail(method) },
      // The bank is PSE's alone. Nequi shows the phone instead, because that number IS the account charged —
      // confirming a Nequi collection without seeing it would leave the one decisive field unverified.
      ...(isPse
        ? [{ label: t("pse_bank_label"), value: bankName || (payer.bankCode ?? "") }]
        : isNequi
          ? [{ label: t("form_nequi_phone_label"), value: payer.phone }]
          : []),
      { label: t("form_payer_name_label"), value: payer.payerName.trim() },
      {
        label: t("pse_document_number_label"),
        value: `${payer.documentType} ${payer.documentNumber}`,
      },
    ];

    return (
      <div className="space-y-6">
        <ColocaStepper steps={stepperSteps} currentStep={stepperCurrent} aria-label={t("create_title")} />
        {confirmViolation && confirmBounds && (
          <ColocaAlert
            variant="destructive"
            icon={AlertCircle}
            text={t(confirmViolation === "above_maximum" ? "confirm_limit_above" : "confirm_limit_below", {
              payer: t(payerType === "COMPANY" ? "limit_payer_company" : "limit_payer_person"),
              max: formatLimitAmount(confirmBounds.max_amount ?? "0", "COP", activeLanguage),
              min: formatLimitAmount(confirmBounds.min_amount ?? "0", "COP", activeLanguage),
            })}
            actions={
              <ColocaButton size="sm" variant="default" onClick={() => setStep("amount")}>
                {t("confirm_limit_edit_amount")}
              </ColocaButton>
            }
          />
        )}
        <PayinConfirmSummary title={t("fee_preview_title")} rows={rows} />
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <ColocaButton variant="ghost" icon={ChevronLeft} onClick={() => setStep("payer")}>
            {t("button_back")}
          </ColocaButton>
          <ColocaButton variant="primary" icon={Save} onClick={handleCreate} disabled={!!confirmViolation}>
            {isPse ? t("submit_pse") : t("submit_payer_rail")}
          </ColocaButton>
        </div>
      </div>
    );
  }

  // ── Payer (every rail but Bre-B) ──
  if (step === "payer") {
    // Unlike the wallet deposit modal, the signed-in user is NOT the payer here: this screen charges a third
    // party. Prefilling their own identity would be wrong, so the form starts empty.
    const payerStepProps = {
      formId: PAYER_FORM_ID,
      defaults: effectivePayer ?? {},
      reference,
      amount: priced.preview ? formatCop(priced.preview.gross_amount) : null,
      currency: "COP",
    };

    return (
      <div className="space-y-6">
        <ColocaStepper steps={stepperSteps} currentStep={stepperCurrent} aria-label={t("create_title")} />
        {isPse ? (
          <PsePayerStep {...payerStepProps} onSubmit={handlePsePayerSubmit} />
        ) : (
          // Deliberately a sibling of the PSE form, not a variant: the PSE one is shaped by the network's
          // certification checklist and must not move when another rail evolves.
          <ColombiaPayerStep
            {...payerStepProps}
            phoneLabel={isNequi ? t("form_nequi_phone_label") : undefined}
            onSubmit={handleColombiaPayerSubmit}
          />
        )}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <ColocaButton variant="ghost" icon={ChevronLeft} onClick={() => setStep("amount")}>
            {t("button_back")}
          </ColocaButton>
          <ColocaButton variant="primary" type="submit" form={PAYER_FORM_ID}>
            {t("button_continue")}
          </ColocaButton>
        </div>
      </div>
    );
  }

  // ── Amount + method ──
  const canSubmitBreb = !!priced.preview && !isSubmitting;

  return (
    <div className="space-y-6">
      {needsPayer && (
        <ColocaStepper steps={stepperSteps} currentStep={stepperCurrent} aria-label={t("create_title")} />
      )}

      <ColocaAlert variant="secondary" icon={METHOD_ICON[method]} text={railHint} />

      {/* Without an organization the preview (and the payin itself) is unbuildable — say it up front,
          since the submit stays disabled until a preview exists. */}
      {!organizationId && <ColocaAlert variant="destructive" icon={AlertCircle} text={t("error_no_org")} />}

      <PayinAmountWithFee
        organizationId={organizationId}
        rail={method}
        country="COL"
        currency="COP"
        labels={labels}
        quickAmounts={quickAmountsFor("COP")}
        initialAmount={priced.amount}
        initialMode={priced.mode}
        payerType={payerType}
        onChange={setPriced}
      />

      <ColombiaPayinMethodChoice
        value={method}
        onChange={setMethod}
        disabled={isSubmitting}
        methods={ALL_COLOMBIA_PAYIN_METHODS}
      />

      {!needsPayer && (
        <BrebReceivingKeySelect value={brebKeyId} onChange={setBrebKeyId} disabled={isSubmitting} />
      )}

      {needsPayer ? (
        <ColocaButton
          variant="primary"
          disabled={!priced.preview}
          onClick={() => setStep("payer")}
          className="w-full"
        >
          {t("button_continue")}
        </ColocaButton>
      ) : (
        <ColocaButton
          variant="primary"
          icon={Save}
          loading={isSubmitting}
          disabled={!canSubmitBreb}
          onClick={handleBrebSubmit}
          className="w-full"
        >
          {priced.mode === "gross" ? t("submit_gross") : t("submit_net")}
        </ColocaButton>
      )}
    </div>
  );
}
